import { Flex, Tooltip } from "@chakra-ui/react";
import moment from "moment";
import { useIntl } from "react-intl";

import { RanaTable } from "@nens/ranalib";
import { displayBytes } from "@nens/shared-utils";

import { CompleteFile } from "@/features/publications";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { Profile } from "@/lib/profile";
import { RanaFileIcon, isPublicationFile } from "@/lib/rana-file-data-type";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { getFiles, getIsEditable } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { usePublicationDetailFileList } from "../hooks/use-file-list";

import { PublicationDetailFileAdd } from "./file-add";
import { FileAddModal } from "./file-add-modal";
import { PublicationDetailFileReferenceNameModal } from "./file-reference-name-modal";
import { PublicationDetailFileVersionModal } from "./file-version-modal";
import { FileRemoveConfirmationModal } from "./remove-confirmation-modal";
import { TableEmptyPublicationFiles } from "./table-empty-files";

function filterFileReference(fileReference: CompleteFile, searchString: string): boolean {
  if (!searchString) return true;

  return fileReference.path.toLowerCase().includes(searchString.toLowerCase());
}

export function PublicationDetailFileList() {
  const intl = useIntl();
  const { searchString, setSearchString } = useRanaTableData({ keyAddon: "publicationFiles" });

  const fileReferences = usePublicationStore(getFiles);
  const isEditable = usePublicationStore(getIsEditable);

  const publicationFileReferences = fileReferences
    .filter((fileReference) => filterFileReference(fileReference, searchString))
    .sort(({ file }) => (isPublicationFile(file) ? -1 : 1));

  const tableDataIds = publicationFileReferences.map(({ path }) => path);

  const { getTableActions, getRowActions, openModal, openAddFileModal, onClose } =
    usePublicationDetailFileList();

  return (
    <>
      <FileAddModal isOpen={openModal?.type === "file-add"} onClose={onClose} />
      {openModal?.type === "file-remove-confirmation" && (
        <FileRemoveConfirmationModal files={openModal.files} onClose={onClose} />
      )}
      {openModal?.type === "file-reference-name" && (
        <PublicationDetailFileReferenceNameModal fileReference={openModal.file} onClose={onClose} />
      )}
      {openModal?.type === "file-version" && (
        <PublicationDetailFileVersionModal fileReference={openModal.file} onClose={onClose} />
      )}
      <RanaTable
        tableActions={getTableActions()}
        hasRowActions
        columnDefinitions={[
          {
            width: "table_cell_sm",
            title: intl.formatMessage({
              id: "publication.detail.file.column.name",
              defaultMessage: "Name",
            }),
            orderingField: "id",
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({
              id: "publication.detail.file.column.size",
              defaultMessage: "Size",
            }),
          },
          {
            title: intl.formatMessage({
              id: "publication.detail.file.column.added_by",
              defaultMessage: "Added by",
            }),
            width: "table_cell_xs",
          },
          {
            title: intl.formatMessage({
              id: "publication.detail.file.column.version",
              defaultMessage: "Version",
            }),
            width: "table_cell_xs",
          },
          {
            width: "table_cell_sm",

            titleRenderFunction: () => (
              <Tooltip
                label={intl.formatMessage({
                  id: "publication.detail.file.column.version_date_tooltip",
                  defaultMessage: "When this file's version was created in the project",
                })}
              >
                {intl.formatMessage({
                  id: "publication.detail.file.column.version_date",
                  defaultMessage: "File version date",
                })}
              </Tooltip>
            ),
          },
        ]}
        tableDataIds={tableDataIds}
        emptyElement={
          <TableEmptyPublicationFiles
            onAddFiles={openAddFileModal}
            hasSearchString={!!searchString}
            clearSearchString={() => setSearchString("")}
          />
        }
      >
        <Flex>
          <RanaTable.Filters
            search={{
              placeholder: intl.formatMessage({
                id: "search.publication_file",
                defaultMessage: "Find file",
              }),
              searchString,
              setSearchString,
            }}
          />
          {isEditable && <PublicationDetailFileAdd />}
        </Flex>

        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {publicationFileReferences.map(({ path, ref, file, name }) => {
              const fallbackName = getNameFromFileId(path);
              const fileSize = displayBytes(file.size);

              return (
                <RanaTable.Row rowActions={getRowActions(path, ref)} key={path} rowId={path}>
                  <RanaTable.Cell title={name ?? fallbackName}>
                    <RanaFileIcon fileOrDirectory={file} />
                    {name ?? fallbackName}
                  </RanaTable.Cell>
                  <RanaTable.Cell title={fileSize || ""}>{fileSize}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <Profile inTable user={file.user} />
                  </RanaTable.Cell>
                  <RanaTable.Cell title={ref}>{ref?.slice(0, 7)}</RanaTable.Cell>
                  <RanaTable.Cell>
                    {moment(file.lastModified).format("dddd, D MMM YYYY, HH:mm")}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </>
  );
}
